import { useState, useMemo } from "react";
import { Link } from "wouter";
import Header from "@/components/layout/header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import {
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  AlertCircle,
  Gift,
  Star,
} from "lucide-react";
import { usePerks, useDeletePerk, useUpdatePerkStatus } from "@/hooks/usePerks";
import { format } from "date-fns";
import type { PerkStatus, CmsPerkResponse } from "@/types/generated/cms.generated";

const statusVariant: Record<PerkStatus, "default" | "secondary" | "outline" | "destructive"> = {
  Active: "default",
  Inactive: "outline",
  Expired: "destructive",
};

export default function PerksPage() {
  const { data: perks = [], isLoading, error } = usePerks();
  const deletePerk = useDeletePerk();
  const updateStatus = useUpdatePerkStatus();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredPerks = useMemo(() => {
    let result = perks;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title?.toLowerCase().includes(query) ||
          p.vendorName?.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((p) => p.status === statusFilter);
    }

    return result;
  }, [perks, searchQuery, statusFilter]);

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Perks" description="Manage global perks for employees">
          <Button disabled>
            <Plus className="w-4 h-4 mr-2" />
            Create Perk
          </Button>
        </Header>
        <div className="flex-1 p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error loading perks</AlertTitle>
            <AlertDescription>Failed to load perks. Please try refreshing the page.</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header title="Perks" description="Manage global perks for employees">
        <Link href="/perks/create">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Create Perk
          </Button>
        </Link>
      </Header>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Stats cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Total Perks</span>
              </div>
              <p className="text-2xl font-bold mt-1">{perks.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Badge variant="default" className="h-4 px-1 text-[10px]">Active</Badge>
                <span className="text-sm text-muted-foreground">Active Perks</span>
              </div>
              <p className="text-2xl font-bold mt-1">{perks.filter((p) => p.status === "Active").length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-sm text-muted-foreground">Featured</span>
              </div>
              <p className="text-2xl font-bold mt-1">{perks.filter((p) => p.isFeatured).length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Total Redemptions</span>
              </div>
              <p className="text-2xl font-bold mt-1">
                {perks.reduce((sum, p) => sum + (p.totalRedemptions || 0), 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search perks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                  <SelectItem value="Expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Perks table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredPerks.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {searchQuery || statusFilter !== "all"
                  ? "No perks match your filters."
                  : "No perks created yet. Create your first perk to get started."}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Perk</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Delivery</TableHead>
                    <TableHead>Coins</TableHead>
                    <TableHead>Redemptions</TableHead>
                    <TableHead>Opt-Outs</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPerks.map((perk) => (
                    <TableRow key={perk.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {perk.imageUrl && (
                            <img
                              src={perk.imageUrl}
                              alt=""
                              className="w-10 h-10 rounded object-cover flex-shrink-0"
                            />
                          )}
                          {perk.isFeatured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />}
                          <div>
                            <p className="font-medium">{perk.title}</p>
                            {perk.description && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {perk.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{perk.vendorName || "-"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={perk.status === "Active"}
                            onCheckedChange={(checked) => {
                              if (perk.id) {
                                updateStatus.mutate({
                                  id: perk.id,
                                  status: checked ? "Active" : "Inactive",
                                });
                              }
                            }}
                            disabled={updateStatus.isPending || perk.status === "Expired"}
                          />
                          <Badge variant={statusVariant[perk.status || "Inactive"]}>
                            {perk.status}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{perk.deliveryMethod}</Badge>
                      </TableCell>
                      <TableCell>{perk.coinCost || 0}</TableCell>
                      <TableCell>{perk.totalRedemptions || 0}</TableCell>
                      <TableCell>{perk.optOutCount || 0}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Link href={`/perks/${perk.id}`}>
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link href={`/perks/${perk.id}/edit`}>
                            <Button variant="ghost" size="sm">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Perk</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{perk.title}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => perk.id && deletePerk.mutate(perk.id)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
